import React, { createContext, useContext, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

// Allows detail pages (e.g. ProgramDetail) to set a human-readable label for the last breadcrumb segment (e.g. program title instead of ID)
const BreadcrumbLabelContext = createContext<{
  label: string | null;
  setLabel: (label: string | null) => void;
}>({ label: null, setLabel: () => {} });

export function BreadcrumbLabelProvider({ children }: { children: React.ReactNode }) {
  const [label, setLabelState] = useState<string | null>(null);
  const setLabel = useCallback((value: string | null) => setLabelState(value), []);
  return (
    <BreadcrumbLabelContext.Provider value={{ label, setLabel }}>
      {children}
    </BreadcrumbLabelContext.Provider>
  );
}

export function useBreadcrumbLabel() {
  return useContext(BreadcrumbLabelContext);
}

// Route-to-label mapping for the Clinic portal
const ROUTE_LABELS: Record<string, string> = {
  '': 'Dashboard',
  'storefront': 'Storefront',
  'program': 'Program',
  'cart': 'Cart',
  'checkout': 'Checkout',
  'practitioners': 'Practitioners',
  'patients': 'Patients',
  'prescriptions': 'Orders',
  'prescription': 'Order',
  'payments': 'Payments',
  'products': 'Products',
  'settings': 'Settings',
  'calendar': 'Calendar',
  'availability': 'Availability',
  'booking': 'Booking',
  'appointment-types': 'Appointment Types',
  'consents': 'Consents',
  'webhooks': 'Webhooks',
  'preferences': 'Preferences',
  'roles': 'Roles',
  'users': 'Users',
  'admins': 'Admins',
  'subscriptions': 'Subscriptions',
  'intakes': 'Intakes',
  'intellihealth': 'IntelliHealth',
  'emr': 'EMR',
  'telehealth': 'Telehealth',
  'call': 'Video Call',
  'messaging': 'Messaging',
  'labs': 'Labs',
  'orders': 'Orders',
  'results': 'Results',
  'commissions': 'Commissions',
};

// Parent segments that group child routes but have no page of their own
const NON_CLICKABLE_SEGMENTS = new Set(['commissions']);

function getLabel(segment: string): string {
  return ROUTE_LABELS[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isIdSegment(segment: string): boolean {
  // UUIDs or numeric IDs
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
    || /^\d+$/.test(segment);
}

export interface BreadcrumbProps {
  className?: string;
}

export function Breadcrumbs({ className }: BreadcrumbProps) {
  const location = useLocation();
  const { label: overrideLabel } = useBreadcrumbLabel();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isStorefrontProgramPath =
    pathSegments.includes('storefront') && pathSegments.includes('program');

  // Don't show breadcrumbs on the root dashboard
  if (pathSegments.length === 0) return null;

  const crumbs: { label: string; path: string; isLast: boolean; clickable: boolean }[] = [];
  let currentPath = '';

  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    const prevSegment = i > 0 ? pathSegments[i - 1] : null;
    currentPath += `/${segment}`;
    const isLast = i === pathSegments.length - 1;
    const isId = isIdSegment(segment);

    if (isStorefrontProgramPath && segment === 'program' && prevSegment === 'storefront') {
      continue;
    }

    if (isStorefrontProgramPath && isId && prevSegment === 'program') {
      if (isLast && overrideLabel) {
        crumbs.push({
          label: overrideLabel,
          path: currentPath,
          isLast,
          clickable: true,
        });
      }
      continue;
    }

    if (isId && isLast) {
      // For patient details, avoid showing placeholder/ID while patient data is still loading.
      // The page sets an override label once patient name is available.
      if (overrideLabel && overrideLabel.trim().length > 0) {
        crumbs.push({
          label: overrideLabel,
          path: currentPath,
          isLast,
          clickable: true,
        });
      } else if (prevSegment !== 'patients' && prevSegment !== 'practitioners') {
        // Show shortened ID for other detail pages when no override
        crumbs.push({
          label: `#${segment.slice(0, 8)}...`,
          path: currentPath,
          isLast,
          clickable: true,
        });
      }
    } else if (isId) {
      // Show shortened ID for detail pages when no override
      crumbs.push({
        label: `#${segment.slice(0, 8)}...`,
        path: currentPath,
        isLast,
        clickable: true,
      });
    } else {
      crumbs.push({
        label: getLabel(segment),
        path: currentPath,
        isLast,
        clickable: !NON_CLICKABLE_SEGMENTS.has(segment),
      });
    }
  }

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center text-xs', className)}>
      <ol className="flex items-center space-x-1">
        <li>
          <Link
            to="/"
            className="flex items-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Home className="h-3.5 w-3.5" />
          </Link>
        </li>

        {crumbs.map((crumb) => (
          <li key={crumb.path} className="flex items-center">
            <ChevronRight className="h-3 w-3 text-slate-300 mx-0.5" />
            {crumb.isLast ? (
              <span className="font-medium text-slate-900 truncate max-w-[200px]" title={crumb.label}>
                {crumb.label}
              </span>
            ) : crumb.clickable ? (
              <Link
                to={crumb.path}
                className="text-slate-500 hover:text-slate-700 transition-colors truncate max-w-[200px]"
                title={crumb.label}
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className="text-slate-500 truncate max-w-[200px]"
                title={crumb.label}
              >
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
